import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { MessagesSquare, Sparkles, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function NegotiationDialog({ devis, onMessageCreated }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState("");
    const [model, setModel] = useState("claude");
    const [target, setTarget] = useState(10);
    const [tone, setTone] = useState("professionnel");
    const [custom, setCustom] = useState("");

    const handleGenerate = async () => {
        setLoading(true);
        setResult("");
        try {
            const r = await api.negotiate(devis.id, {
                model,
                target_savings_percent: parseFloat(target) || 10,
                tone,
                custom_context: custom || null,
            });
            setResult(r.message);
            onMessageCreated && onMessageCreated();
        } catch (e) {
            toast.error("Erreur lors de la génération du message");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(result);
        toast.success("Copié dans le presse-papiers");
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    data-testid={`negotiate-btn-${devis.id}`}
                    variant="outline"
                    className="rounded-none border-accent text-accent hover:bg-accent hover:text-white"
                >
                    <MessagesSquare
                        className="w-4 h-4 mr-2"
                        strokeWidth={1.6}
                    />
                    Négocier
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-ink max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                        <Sparkles
                            className="w-5 h-5 text-accent"
                            strokeWidth={1.6}
                        />
                        Générer un message de négociation
                    </DialogTitle>
                </DialogHeader>
                <div className="text-xs text-ink-soft border-l-2 border-accent pl-3 mb-2">
                    Devis : <b>{devis.contractor_name}</b> ({devis.trade}) —{" "}
                    {devis.line_items?.length || 0} postes
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <Label className="stat-label">Modèle IA</Label>
                        <Select value={model} onValueChange={setModel}>
                            <SelectTrigger className="rounded-none mt-1.5">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                                <SelectItem value="claude">
                                    Claude Sonnet 4.5
                                </SelectItem>
                                <SelectItem value="openai">GPT-5.2</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="stat-label">Objectif (% économie)</Label>
                        <Input
                            type="number"
                            className="rounded-none mt-1.5 num"
                            value={target}
                            onChange={(e) => setTarget(e.target.value)}
                            data-testid="negotiate-target"
                        />
                    </div>
                    <div>
                        <Label className="stat-label">Ton</Label>
                        <Select value={tone} onValueChange={setTone}>
                            <SelectTrigger className="rounded-none mt-1.5">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                                <SelectItem value="professionnel">
                                    Professionnel
                                </SelectItem>
                                <SelectItem value="direct">Direct</SelectItem>
                                <SelectItem value="amical">Amical</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div>
                    <Label className="stat-label">
                        Contexte additionnel (optionnel)
                    </Label>
                    <Textarea
                        rows={2}
                        className="rounded-none mt-1.5"
                        value={custom}
                        placeholder="Ex : nous avons reçu une offre plus basse pour le terrassement, le chantier démarre dans 3 semaines…"
                        onChange={(e) => setCustom(e.target.value)}
                    />
                </div>
                <Button
                    data-testid="negotiate-generate"
                    className="rounded-none bg-accent hover:bg-accent-hover w-full"
                    onClick={handleGenerate}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Génération en cours…
                        </>
                    ) : (
                        <>
                            <Sparkles
                                className="w-4 h-4 mr-2"
                                strokeWidth={1.6}
                            />
                            Générer le message
                        </>
                    )}
                </Button>

                {result && (
                    <div className="mt-2 border border-ink bg-[#FAFAFA]">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                            <span className="stat-label">
                                Brouillon généré
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-none h-7"
                                onClick={copyToClipboard}
                            >
                                <Copy className="w-3.5 h-3.5 mr-1.5" />
                                Copier
                            </Button>
                        </div>
                        <pre className="px-3 py-3 text-sm font-sans whitespace-pre-wrap leading-relaxed">
                            {result}
                        </pre>
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => setOpen(false)}
                    >
                        Fermer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
